import * as ko from "knockout";
import { merge } from "ramda";
import * as Rx from "rxjs";
import { Scheduler } from "rxjs/Scheduler";

import { ItemFilterBuilder, defaultItemFilterBuilder } from "../ko-grid/itemFilter";
import { Disposable, ActionDisposable } from "../util/disposable";
import { createSearchRegexFor } from "../util/string";
import { fromKnockoutObservable } from "../util/rx";

import { KnockoutDataSearchInfo } from "./_knockoutDataSearchInfo";
import { DataProvider, DataProviderRequest, DataProviderResponse, QueryOrigin, emptyValue } from "./dataProvider";
import { getDebouncedResponsesFor, DebouncedStreamSettings, defaultDebounceTime } from "./shared";


/** Cold observable stream containing the empty value. */
const emptyValueStream: Rx.Observable<DataProviderResponse> = Rx.Observable.of(emptyValue);


/**
 * Settings for KnockoutDataProvider.
 */
export interface KnockoutDataProviderSettings {

    /**
     * Number of milliseconds to use when debouncing the search stream.
     *
     * @default 250
     */
    debounceTime?: number;

    /**
     * Item index for the primary display value.
     */
    displayIndex: number|string;

    /**
     * Whether data searches should be case insensitive.
     */
    exactMatchIgnoresCase: boolean;

    /**
     * Maximum number of items to display when filtering.
     */
    filterLimit?: number;

    /**
     * Custom ItemFilterBuilder implementation.
     */
    itemFilterBuilder?: ItemFilterBuilder;

    /**
     * Item index used for searching via "itemFilterBuilder".
     */
    searchIndex: number|string;

    /**
     * Scheduler to use for observing source data.
     *
     * @default undefined (rxjs default scheduler)
     */
    scheduler?: Scheduler;

}


/**
 * Possible results for 'findItem'.
 */
export enum MatchResult {

    /**
     * No matching item was found.
     */
    NoMatch,

    /**
     * A single item was found.
     */
    ExactMatch,

    /**
     * Multiple matching items were found.
     */
    MultipleMatches

}


/**
 * Data provider implementation for wrapping a knockout observable.
 */
export class KnockoutDataProvider implements DataProvider {

    /**
     * KnockoutDataProvider constructor.
     *
     * @param source
     *     Knockout observable containing a DataProviderResponse object.
     * @param settings
     *     Settings controlling the behaviour of the data provider.
     */
    constructor(source: ko.MaybeObservable<DataProviderResponse>, settings: KnockoutDataProviderSettings) {
        this._debounceTime = typeof settings.debounceTime === "number" ? settings.debounceTime : defaultDebounceTime;
        this._itemFilterBuilder = settings.itemFilterBuilder || defaultItemFilterBuilder;
        this._lastSearch = new KnockoutDataSearchInfo(settings.displayIndex, settings.exactMatchIgnoresCase);
        this._limit = settings.filterLimit || 7;
        this._searchIndex = settings.searchIndex;
        this._stream =
            (ko.isObservable(source)
                ? fromKnockoutObservable(source)
                : Rx.Observable.of(source))
            .map(r => r || emptyValue); // normalize undefined/null results
        this.scheduler = settings.scheduler;

        // Explicitly subscribe to _stream to ensure that "findItem" will
        // always have the latest data. This is needed for legacy reasons.
        this._stream.subscribe(r => {
            this._lastSearch.data = r.data;
            this._lastSearch.reset();
        });
    }


    // -------------------------------------------------------------------------
    // Public properties
    // -------------------------------------------------------------------------

    /** Rx scheduler for search streams. */
    scheduler: Scheduler;


    // -------------------------------------------------------------------------
    // Public actions
    // -------------------------------------------------------------------------

    /**
     * Finds an item by its identifier value.
     *
     * @param value
     *     Identifier to search for based on the provider's searchIndex value.
     * @returns
     *     Object containing the found item (if exactly one), the match descriptor,
     *     and the search result.
     */
    findItem(value: string): { item: any, match: MatchResult, result: any } {
        const result = this._lastSearch.search(value);

        const item = (result !== false && this._lastSearch.exactMatch && value !== null
            ? this._lastSearch.getFoundRow(null)
            : null);

        const match = (result !== false && this._lastSearch.exactMatch
            ? MatchResult.ExactMatch
            : (this._lastSearch.nextIndex === -1
                ? MatchResult.NoMatch
                : MatchResult.MultipleMatches));

        return { item, match, result };
    }

    /**
     * Gets a stream of query results from the given stream of queries.
     *
     * For Initial- or Grid-origin queries, uses the itemFilterBuilder as
     * a filtering method; for Select-origin queries, uses the (simpler) prefix
     * filtering method.
     *
     * @param requestStream
     *     Observable stream of incoming filter requests.
     * @param origin?
     *     The origin of the filter request.
     * @returns
     *     Observable stream of search results.
     */
    search(requestStream: Rx.Observable<DataProviderRequest>, origin: QueryOrigin = QueryOrigin.Select): Rx.Observable<DataProviderResponse> {
        const settings: DebouncedStreamSettings = {
            scheduler: this.scheduler,
            debounceTime: origin === QueryOrigin.Grid ? this._debounceTime : 0
        };

        return getDebouncedResponsesFor(
            Rx.Observable.combineLatest(this._stream, requestStream),
            ([response, request]) => this._filterResponse(response, request, origin),
            settings);
    }


    // -------------------------------------------------------------------------
    // Protected
    // -------------------------------------------------------------------------

    /**
     * Filters a data provider response based on a filter string and the query origin.
     *
     * Merges request context, if any, into response.
     */
    protected _filterResponse(response: DataProviderResponse, request: DataProviderRequest, origin: QueryOrigin): Rx.Observable<DataProviderResponse> {
        let responseStream: Rx.Observable<DataProviderResponse> = null;

        switch (origin) {
        case QueryOrigin.Initial:
        case QueryOrigin.Grid:
            responseStream = request.filter
                ? this._filterByBuilder(response, request.filter)
                : Rx.Observable.of(response);
            break;
        case QueryOrigin.Select:
            responseStream = this._filterByPrefix(response, request.filter);
            break;
        default:
            throw new Error(`Unsupported QueryOrigin value: "${origin}"`);
        }

        return responseStream.map(r => request.context ? merge(r, { context: request.context }) : r);
    }


    /** Cold Rx stream of source data. */
    protected _stream: Rx.Observable<DataProviderResponse>;


    // -------------------------------------------------------------------------
    // Private
    // -------------------------------------------------------------------------

    /**
     * Filter using the ItemFilterBuilder set for this provider instance.
     */
    protected _filterByBuilder(response: DataProviderResponse, filter: string): Rx.Observable<DataProviderResponse> {
        const data = response.data;

        // picklist data is not yet available
        if (data === null) {
            return emptyValueStream;
        }
        const itemFilter = this._itemFilterBuilder(data.items, [this._searchIndex]);
        const itemFilterStream = Rx.Observable.bindCallback(itemFilter);

        return itemFilterStream(filter).map<DataProviderResponse>(items => ({
            data: merge(data, { items, resultCount: items.length }),
            error: null
        }));
    }

    /**
     * Fast filter that does a prefix match against the searchable column.
     */
    protected _filterByPrefix(response: DataProviderResponse, filter: string = ""): Rx.Observable<DataProviderResponse> {
        const data = response.data;

        // picklist data is not yet available
        if (data === null) {
            return emptyValueStream;
        }

        // picklist data is available, find all matches
        const regex = createSearchRegexFor(filter);
        const items: any[] = [];
        let i = -1;
        let moreResults = false;
        for ( ; ; ) {
            i = this._lastSearch.findNextMatch(regex, i + 1);
            if (i === -1) { break; }
            if (this._limit > 0 && items.length >= this._limit) {
                moreResults = true;
                break;
            }

            items.push(data.items[i]);
        }

        return Rx.Observable.of({
            data: merge(data, { items, resultCount: moreResults ? items.length + 1 : items.length }),
            error: null
        });
    }

    /** Number of milliseconds to use when debouncing a search stream. */
    private _debounceTime: number;

    /** Function that generates an ItemFilter for Initial- or Grid-origin queries. */
    private _itemFilterBuilder: ItemFilterBuilder;

    /** Helper class for findItem(). */
    private _lastSearch: KnockoutDataSearchInfo;

    /** Maximum number of results to include in Select-origin queries. */
    private _limit: number;

    /** Item index to match when filtering. */
    private _searchIndex: string|number;

}


/**
 * Defines Knockout data provider constructor shape.
 */
export interface KnockoutDataProviderClass {
    new(source: ko.Observable<DataProviderResponse>, settings: KnockoutDataProviderSettings): DataProvider;
}


/**
 * Current Knockout data provider class. Updated by "useKnockoutDataProviderClass".
 */
let currentKnockoutDataProviderClass: KnockoutDataProviderClass = KnockoutDataProvider;


/**
 * Creates an instance of current Knockout data provider (an override specified by "useKnockoutDataProviderClass" or "KnockoutDataProvider" if no override is active).
 */
export function createKnockoutDataProvider(source: ko.Observable<DataProviderResponse>, settings: KnockoutDataProviderSettings): DataProvider {
    return new currentKnockoutDataProviderClass(source, settings);
}


/**
 * Replaces default Knockout data provider class with the specified class until the returned disposable is disposed.
 */
export function useKnockoutDataProviderClass(overrideClass: KnockoutDataProviderClass): Disposable {
    if (currentKnockoutDataProviderClass !== KnockoutDataProvider) {
        throw new Error("Knockout data provider class has already been overridden, unable to override again.");
    }
    if (typeof overrideClass !== "function") {
        throw new Error("Knockout data provider override class was expected to be a JavaScript function.");
    }

    currentKnockoutDataProviderClass = overrideClass;
    return new ActionDisposable(() => currentKnockoutDataProviderClass = KnockoutDataProvider);
}



// WEBPACK FOOTER //
// /Users/johng/dev/web-pli/data-provider/knockoutDataProvider.ts